import React from 'react'; // Ajoute useState si pas déjà
import { Table, Tag, Button, Space, Tooltip, Modal, message } from 'antd';
import { 
  EyeOutlined, 
  EditOutlined, 
  CheckCircleOutlined,
  StopOutlined,
  UserDeleteOutlined
} from '@ant-design/icons';
// Import de la modale

const UsersTable = ({ 
  users = [], 
  pagination, 
  loading, 
  onView, 
  onEditRole, 
  onUpdateStatus,
  onTableChange,
  onUserUpdated
}) => {

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'suspended': return 'warning';
      case 'banned': return 'error';
      case 'pending': return 'processing';
      default: return 'default';
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'red';
      case 'manager': return 'orange';
      case 'leader': return 'blue';
      case 'user': return 'green';
      default: return 'default';
    }
  };

  const getVerificationColor = (status) => {
    switch (status) {
      case 'verified': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const handleViewDetails = (user) => {
    onView(user);
  };

  // Fonction corrigée pour changer le statut
  const handleStatusChange = async (user, newStatus) => {
    try {
      if (!onUpdateStatus) {
        message.error('Fonction onUpdateStatus non disponible');
        return;
      }

      // Demander confirmation
      Modal.confirm({
        title: `Changer le statut de ${user.username}`,
        content: `Voulez-vous vraiment passer de "${user.account_status?.toUpperCase()}" à "${newStatus.toUpperCase()}" ?`,
        okText: 'Confirmer',
        cancelText: 'Annuler',
        onOk: async () => {
          const result = await onUpdateStatus(user.id, newStatus);
          
          if (result && result.success) {
            message.success(`Statut mis à jour: ${newStatus}`);
            if (onUserUpdated) {
              onUserUpdated({ ...user, account_status: newStatus });
            }
          } else {
            message.error(result?.message || 'Échec de la mise à jour');
          }
        }
      });
    } catch (error) {
      console.error('Erreur lors du changement de statut:', error);
      message.error('Erreur lors du changement de statut');
    }
  };

  // Déterminer quel statut montrer selon l'état actuel
  const getStatusButton = (record) => {
    const currentStatus = record.account_status;
    
    if (currentStatus === 'active') {
      return (
        <Tooltip title="Suspendre">
          <Button
            danger
            shape="circle"
            icon={<StopOutlined />}
            onClick={() => handleStatusChange(record, 'suspended')}
            size="small"
          />
        </Tooltip>
      );
    } else if (currentStatus === 'suspended') {
      return (
        <Space size="small">
          <Tooltip title="Activer">
            <Button
              type="primary"
              shape="circle"
              icon={<CheckCircleOutlined />}
              onClick={() => handleStatusChange(record, 'active')}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Bannir">
            <Button
              danger
              shape="circle"
              icon={<UserDeleteOutlined />}
              onClick={() => handleStatusChange(record, 'banned')}
              size="small"
            />
          </Tooltip>
        </Space>
      );
    } else if (currentStatus === 'banned') {
      return (
        <Tooltip title="Réactiver">
          <Button
            type="primary"
            shape="circle"
            icon={<CheckCircleOutlined />}
            onClick={() => handleStatusChange(record, 'active')}
            size="small"
          />
        </Tooltip>
      );
    } else {
      // Statut inconnu ou 'pending'
      return (
        <Tooltip title="Activer">
          <Button
            type="primary"
            shape="circle"
            icon={<CheckCircleOutlined />}
            onClick={() => handleStatusChange(record, 'active')}
            size="small"
          />
        </Tooltip>
      );
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: true,
    },
    {
      title: 'Nom d\'utilisateur',
      dataIndex: 'username',
      key: 'username',
      sorter: true,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: true,
    },
    {
      title: 'Téléphone',
      dataIndex: 'phone_number',
      key: 'phone_number',
      render: (phone) => phone || 'Non renseigné',
    },
    {
      title: 'Telegram',
      dataIndex: 'telegram_username',
      key: 'telegram_username',
      render: (username) => username ? (
        <a href={`https://t.me/${username.replace('@', '')}`} target="_blank" rel="noreferrer" className="text-indigo-400 font-semibold">
          {username.startsWith('@') ? username : `@${username}`}
        </a>
      ) : 'Non lié',
    },
    {
      title: 'Rôle',
      dataIndex: 'role',
      key: 'role',
      render: (role) => (
        <Tag color={getRoleColor(role)}>
          {role?.toUpperCase() || 'USER'}
        </Tag>
      ),
      filters: [
        { text: 'Admin', value: 'admin' },
        { text: 'Manager', value: 'manager' },
        { text: 'Leader', value: 'leader' },
        { text: 'User', value: 'user' },
      ],
    },
    {
      title: 'Statut',
      dataIndex: 'account_status',
      key: 'account_status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status?.toUpperCase() || 'ACTIVE'}
        </Tag>
      ),
      filters: [
        { text: 'Actif', value: 'active' },
        { text: 'Suspendu', value: 'suspended' },
        { text: 'Banni', value: 'banned' },
        { text: 'En attente', value: 'pending' },
      ],
    },
    {
      title: 'Vérification',
      dataIndex: 'kyc_status',
      key: 'kyc_status',
      render: (status) => (
        <Tag color={getVerificationColor(status)}>
          {status?.toUpperCase() || 'PENDING'}
        </Tag>
      ),
    },
    {
      title: 'Date d\'inscription',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString('fr-FR'),
      sorter: true,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => {
        return (
          <Space key={record.id || `${record.username}-${record.email}`} size="small">
            <Tooltip title="Voir détails">
              <Button
                type="primary"
                shape="circle"
                icon={<EyeOutlined />}
                onClick={() => handleViewDetails(record)}
                size="small"
              />
            </Tooltip>
            
            <Tooltip title="Modifier le rôle">
              <Button
                type="default"
                shape="circle"
                icon={<EditOutlined />}
                onClick={() => onEditRole && onEditRole(record)}
                size="small"
              />
            </Tooltip>
            
            {/* Bouton de changement de statut */}
            {getStatusButton(record)}
          </Space>
        );
      },
    },
  ];

  const dataSource = users.map(user => ({
    ...user,
    key: user.id || `${user.username}-${user.email}`
  }));

  const tablePagination = {
    current: pagination?.page || 1,
    pageSize: pagination?.limit || 50,
    total: pagination?.total || 0,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50', '100'],
    showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total} utilisateurs`,
  };

  return (
    <>
    <div className="glass-card" style={{ padding: '24px' }}>
      <Table
        columns={columns}
        dataSource={dataSource}
        pagination={tablePagination}
        loading={loading}
        onChange={onTableChange}
        scroll={{ x: 1200 }}
        locale={{ emptyText: 'Aucun utilisateur trouvé' }}
      />
    </div>
      
      {/* Modale de détails */}
    </>
  );
};

export default UsersTable;